import { EditIcon, PlusIcon, TrashIcon } from "@xola/icons";
import { ButtonGroup, flash, Spinner, Switch, Table, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isPast, parseTime } from "common";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { Experience, OpeningHour, Weekday } from "types";
import { DAY_LABELS, OpeningHoursModal } from "./OpeningHoursModal";
import { api } from "@/api/api";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";

interface OpeningHoursSectionProps {
    experience: Experience;
    pendingDisable?: boolean;
    requiredByEarlyCutoff?: boolean;
    onPendingDisableChange?: (disabled: boolean, schedulesToDelete?: OpeningHour[]) => void;
}

enum ScheduleFilter {
    ACTIVE = 0,
    EXPIRED = 1,
    ALL = 2,
}

const WEEKDAY_DISPLAY_ORDER: Weekday[] = [
    Weekday.MONDAY,
    Weekday.TUESDAY,
    Weekday.WEDNESDAY,
    Weekday.THURSDAY,
    Weekday.FRIDAY,
    Weekday.SATURDAY,
    Weekday.SUNDAY,
];

const getDayHoursLabel = (entry: OpeningHour, day: Weekday): string => {
    const dayHours = entry.openingHoursPerDay.find((entryDay) => entryDay.day === day);
    if (!dayHours) {
        return "Closed";
    }

    return `${formatHhmmDisplay(dayHours.timeRange.startTime)} - ${formatHhmmDisplay(dayHours.timeRange.endTime)}`;
};

export const OpeningHoursSection = ({
    experience: initExperience,
    pendingDisable = false,
    requiredByEarlyCutoff = false,
    onPendingDisableChange,
}: OpeningHoursSectionProps) => {
    const [updatedExperience, setUpdatedExperience] = useState<Experience>();
    const experience = updatedExperience ?? initExperience;
    const experienceId = experience.id;
    const invalidate = useInvalidate();

    // Build a stable hash from schedule IDs + updatedAt so the effect only fires when
    // the server data genuinely changes, not on incidental reference changes from React Query.
    const scheduleHash = useMemo(
        () => (initExperience.openingHours ?? []).map((s) => `${s.id ?? ""}:${s.updatedAt ?? ""}`).join("|"),
        [initExperience.openingHours],
    );

    useEffect(() => {
        setUpdatedExperience(undefined);
    }, [initExperience.id, scheduleHash]);

    const openingHours = experience.openingHours ?? [];
    const [activeFilter, setActiveFilter] = useState(ScheduleFilter.ACTIVE);
    const [editing, setEditing] = useState<OpeningHour>();
    const [deletingScheduleId, setDeletingScheduleId] = useState<string>();
    const modal = useModal(false);
    const isEnabled = (openingHours.length > 0 || modal.isVisible) && !pendingDisable;

    const activeSchedules = openingHours.filter((entry) => isActive(entry));
    const expiredSchedules = openingHours.filter((entry) => isExpired(entry));

    const filterTabs: Array<{ filter: ScheduleFilter; label: string; count: number }> = [
        { filter: ScheduleFilter.ACTIVE, label: "Active", count: activeSchedules.length },
        { filter: ScheduleFilter.EXPIRED, label: "Expired", count: expiredSchedules.length },
        { filter: ScheduleFilter.ALL, label: "All", count: openingHours.length },
    ];

    const visibleFilter = resolveVisibleFilter(activeFilter, activeSchedules, expiredSchedules, openingHours);
    const visibleSchedules =
        visibleFilter === ScheduleFilter.ACTIVE
            ? activeSchedules
            : visibleFilter === ScheduleFilter.EXPIRED
            ? expiredSchedules
            : openingHours;

    const { mutateAsync: createEditSchedule } = useMutation(async (payload: OpeningHour) => {
        const { data } = await (payload.id
            ? api.put<OpeningHour>(`/experiences/${experienceId}/openingHours/${payload.id}`, payload)
            : api.post<OpeningHour>(`/experiences/${experienceId}/openingHours`, payload));

        setUpdatedExperience((previous) => {
            const base = previous ?? experience;
            const previousSchedules = base.openingHours ?? [];
            const nextSchedules = payload.id
                ? previousSchedules.map((schedule) => (schedule.id === payload.id ? data : schedule))
                : [...previousSchedules, data];
            return { ...base, openingHours: nextSchedules };
        });
        invalidate("experiences", experienceId);
        return data;
    });

    const { mutateAsync: deleteSchedule } = useMutation(async (scheduleId: string) => {
        await api.delete(`/experiences/${experienceId}/openingHours/${scheduleId}`);
        setUpdatedExperience((previous) => {
            const base = previous ?? experience;
            const previousSchedules = base.openingHours ?? [];
            return {
                ...base,
                openingHours: previousSchedules.filter((schedule) => schedule.id !== scheduleId),
            };
        });
        invalidate("experiences", experienceId);
    });

    const handleEnableToggle = (checked: boolean) => {
        if (checked) {
            onPendingDisableChange?.(false);
            if (openingHours.length === 0) {
                setEditing(undefined);
                modal.show();
            }
            return;
        }

        modal.hide();
        setEditing(undefined);
        onPendingDisableChange?.(true, openingHours);
    };

    const handleAdd = () => {
        setEditing(undefined);
        modal.show();
    };

    const handleEdit = (entry: OpeningHour) => {
        setEditing(entry);
        modal.show();
    };

    const handleDelete = async (entry: OpeningHour) => {
        if (!entry.id) {
            return;
        }
        setDeletingScheduleId(entry.id);
        try {
            await deleteSchedule(entry.id);
            flash.success("Business hours entry deleted");
        } catch {
            flash.danger("Failed to delete. Please try again.");
        } finally {
            setDeletingScheduleId(undefined);
        }
    };

    const handleApply = async (entry: OpeningHour) => {
        await createEditSchedule(entry);
        modal.hide();
        setEditing(undefined);
        flash.success("Business hours saved");
    };

    const handleClose = () => {
        modal.hide();
        setEditing(undefined);
    };

    const handleFilterChange = (button: number) => {
        setActiveFilter(button as ScheduleFilter);
    };

    const siblingsForModal = editing ? openingHours.filter((schedule) => schedule.id !== editing.id) : openingHours;

    return (
        <div className="flex w-full min-w-0 flex-col space-y-4">
            <div className="flex items-center space-x-3">
                {requiredByEarlyCutoff ? (
                    <Tooltip
                        content={
                            <span>
                                To disable business hours, change the <b>Early Cutoff</b> setting to a value other than{" "}
                                <b>end of business</b>.
                            </span>
                        }
                    >
                        <span>
                            <Switch
                                disabled
                                size="medium"
                                data-testid="business-hours-enable-switch"
                                isChecked={isEnabled}
                                onChange={handleEnableToggle}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <Switch
                        size="medium"
                        data-testid="business-hours-enable-switch"
                        isChecked={isEnabled}
                        onChange={handleEnableToggle}
                    />
                )}
                <span>Enable business hours</span>
            </div>

            {isEnabled && (
                <div className="flex flex-col space-y-3">
                    <div className="flex items-center justify-between">
                        {openingHours.length > 0 ? (
                            <ButtonGroup size="small" value={visibleFilter} onChange={handleFilterChange}>
                                {filterTabs.map(({ filter, label, count }) => (
                                    <ButtonGroup.Button
                                        key={filter}
                                        type="button"
                                        className={clsx(count > 0 ? "visible" : "hidden")}
                                    >
                                        {label} <NoTranslate>({count})</NoTranslate>
                                    </ButtonGroup.Button>
                                ))}
                            </ButtonGroup>
                        ) : (
                            <div />
                        )}

                        <SecondaryButton size="small" icon={<PlusIcon />} onClick={handleAdd}>
                            Add Date Range
                        </SecondaryButton>
                    </div>

                    {visibleSchedules.length === 0 ? (
                        <div className="rounded border border-dashed border-gray-light p-4 text-center text-sm ">
                            No business hours in this view.
                        </div>
                    ) : (
                        <div className="max-h-[500px] overflow-y-auto py-2">
                            <Table>
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Header className="whitespace-nowrap !text-sm">Date Range</Table.Header>
                                        <Table.Header className="!text-sm">Business Hours</Table.Header>
                                        <Table.Header className="whitespace-nowrap text-right !text-sm">
                                            Actions
                                        </Table.Header>
                                    </Table.Row>
                                </Table.Head>

                                <Table.Body>
                                    {visibleSchedules.map((entry) => {
                                        const isDeletingRow = deletingScheduleId === entry.id;

                                        return (
                                            <Table.Row key={entry.id ?? `${entry.start}-${entry.end ?? "none"}`}>
                                                <Table.Cell className="align-top">
                                                    <NoTranslate as="div" className="whitespace-normal font-medium">
                                                        {formatDate(entry.start, DateFormat.DATE_SHORT)} -{" "}
                                                        {entry.end
                                                            ? formatDate(entry.end, DateFormat.DATE_SHORT)
                                                            : "No end"}
                                                    </NoTranslate>
                                                    {entry.name ? <div className="text-xs ">{entry.name}</div> : null}
                                                </Table.Cell>
                                                <Table.Cell className="align-top">
                                                    <div className="flex flex-col space-y-0.5">
                                                        {WEEKDAY_DISPLAY_ORDER.map((day) => (
                                                            <div key={day} className="flex items-center gap-2">
                                                                <span className="w-24 shrink-0">{DAY_LABELS[day]}</span>
                                                                <NoTranslate as="span">
                                                                    {getDayHoursLabel(entry, day)}
                                                                </NoTranslate>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell className="align-top">
                                                    <div className="flex items-start justify-end space-x-2 pt-0.5">
                                                        <SecondaryButton
                                                            size="tiny"
                                                            title="Edit"
                                                            onClick={() => handleEdit(entry)}
                                                        >
                                                            <EditIcon />
                                                        </SecondaryButton>
                                                        <SecondaryButton
                                                            size="tiny"
                                                            title="Delete"
                                                            disabled={isDeletingRow}
                                                            onClick={async () => handleDelete(entry)}
                                                        >
                                                            {isDeletingRow ? <Spinner size="tiny" /> : <TrashIcon />}
                                                        </SecondaryButton>
                                                    </div>
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                        </div>
                    )}
                </div>
            )}

            <OpeningHoursModal
                isOpen={modal.isVisible}
                initialValue={editing}
                siblings={siblingsForModal}
                onClose={handleClose}
                onApply={handleApply}
            />
        </div>
    );
};

const resolveVisibleFilter = (
    activeFilter: ScheduleFilter,
    activeSchedules: OpeningHour[],
    expiredSchedules: OpeningHour[],
    allSchedules: OpeningHour[],
): ScheduleFilter => {
    const currentFilterHasItems =
        (activeFilter === ScheduleFilter.ACTIVE && activeSchedules.length > 0) ||
        (activeFilter === ScheduleFilter.EXPIRED && expiredSchedules.length > 0) ||
        (activeFilter === ScheduleFilter.ALL && allSchedules.length > 0);

    if (currentFilterHasItems) {
        return activeFilter;
    }

    if (activeSchedules.length > 0) {
        return ScheduleFilter.ACTIVE;
    }

    if (expiredSchedules.length > 0) {
        return ScheduleFilter.EXPIRED;
    }

    return ScheduleFilter.ALL;
};

const formatHhmmDisplay = (value: number | null): string => {
    if (value === null) {
        return "Closed";
    }
    const { hours, minutes } = parseTime(value);
    const period = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`;
};

const isExpired = (entry: OpeningHour): boolean => {
    if (!entry.end) {
        return false;
    }
    return isPast(entry.end, "day");
};

const isActive = (entry: OpeningHour): boolean => {
    return !isExpired(entry);
};
